export interface ProductFeatureGroup {
  title: string;
  items: string[];
}

export interface ProductUseCase {
  title: string;
  description: string;
}

export interface ProductPricingTier {
  name: string;
  price: string;
  description: string;
  features: string[];
}

export interface Product {
  id: string;
  name: string;
  nameEn: string;
  tagline: string;
  taglineEn: string;
  description: string;
  price: string;
  unit: string;
  priceAnnual: string;
  features: ProductFeatureGroup[];
  useCases: ProductUseCase[];
  pricingTiers: ProductPricingTier[];
  highlight: boolean;
  emoji: string;
  color: string;
  colorLight: string;
}

export const PRODUCTS: Record<string, Product> = {
  zhixiaoke: {
    id: 'zhixiaoke',
    name: '智小客',
    nameEn: 'ZhiXiaoKe',
    tagline: 'AI客服机器人',
    taglineEn: 'AI Customer Service Bot',
    description: '多渠道接入（微信/网站），7×24小时自动回复，FAQ智能分流，订单查询一键完成。人工接管无缝切换，数据报表实时分析。',
    price: '2,980',
    unit: '元/月',
    priceAnnual: '30,000',
    features: [
      {
        title: '核心功能',
        items: [
          '微信/网站双渠道接入',
          'FAQ智能回复与语义理解',
          '订单查询自动化',
          '人工接管无缝切换',
          '多轮对话管理',
        ],
      },
      {
        title: '数据分析',
        items: [
          '日/周/月数据报表',
          '用户满意度追踪',
          '热门问题分析',
          '客服响应效率统计',
        ],
      },
      {
        title: '企业版额外功能',
        items: [
          '自定义知识库管理',
          'API开放接口',
          '私有化部署选项',
          '专属客户成功经理',
        ],
      },
    ],
    useCases: [
      { title: '电商客服', description: '处理订单查询、退换货咨询、促销活动解答，释放人工客服精力处理复杂问题' },
      { title: '在线教育', description: '回答课程咨询、报名流程、课后辅导等常见问题，提升用户满意度' },
      { title: '本地生活服务', description: '餐饮、美容、家政等行业预约咨询、营业时间回复、服务介绍' },
    ],
    pricingTiers: [
      { name: '基础版', price: '2,980', description: '适合初创型商户', features: ['单渠道接入', '基础FAQ回复', '100条/日对话', '标准报表'] },
      { name: '专业版', price: '5,800', description: '适合成长期企业', features: ['双渠道接入', '高级语义理解', '无限对话', '完整报表', '人工接管'] },
      { name: '企业版', price: '12,000', description: '适合规模化企业', features: ['全渠道接入', '私有化部署', 'API接口', '专属顾问', '年度维保'] },
    ],
    highlight: false,
    emoji: '💬',
    color: 'blue',
    colorLight: 'bg-blue-50 border-blue-200',
  },
  xiaoxiaobang: {
    id: 'xiaoxiaobang',
    name: '销小帮',
    nameEn: 'XiaoXiaoBang',
    tagline: 'AI销售助手',
    taglineEn: 'AI Sales Assistant',
    description: '客户画像分析，智能话术推荐，商机构建与跟进提醒，助力销售团队高效成单。销售漏斗实时追踪，竞品对比分析。',
    price: '5,800',
    unit: '元/月',
    priceAnnual: '58,000',
    features: [
      {
        title: '获客与线索',
        items: [
          '潜在客户智能识别',
          '客户画像自动生成',
          '线索评分与优先级排序',
          '多渠道线索汇总',
        ],
      },
      {
        title: '销售赋能',
        items: [
          'AI话术推荐引擎',
          '话术库管理与优化',
          '实时语音转写分析',
          '竞品对比话术库',
        ],
      },
      {
        title: '跟进与管理',
        items: [
          '商机跟进提醒',
          '销售漏斗看板',
          '团队业绩排名',
          '预测成交额分析',
        ],
      },
    ],
    useCases: [
      { title: 'B2B销售团队', description: '销售线索管理、话术支持、商机跟进、外贸询盘处理，提升询盘转化率30%以上' },
      { title: '房产经纪', description: '客户需求分析、房源匹配推荐、跟进提醒、交易流程辅助' },
      { title: '招商加盟', description: '意向客户筛选、加盟政策解答、项目介绍、线下活动邀约' },
    ],
    pricingTiers: [
      { name: '团队版', price: '5,800', description: '适合小型销售团队（≤5人）', features: ['5个账号', '基础话术库', '商机管理', '标准报表'] },
      { name: '企业版', price: '12,000', description: '适合中型销售组织（≤20人）', features: ['20个账号', '高级话术库', 'API接口', '预测分析', '优先支持'] },
      { name: '集团版', price: '28,000', description: '适合大型企业', features: ['无限账号', '私有化部署', '深度定制', '专属客户经理', '年度战略咨询'] },
    ],
    highlight: true,
    emoji: '📈',
    color: 'purple',
    colorLight: 'bg-purple-50 border-purple-200',
  },
  banxiaozhu: {
    id: 'banxiaozhu',
    name: '办小助',
    nameEn: 'BanXiaoZhu',
    tagline: 'AI办公助理',
    taglineEn: 'AI Office Assistant',
    description: '会议纪要自动生成，审批流程自动化，报表智能整理，大幅提升内部协作效率。企业知识库智能检索，工作任务分配提醒。',
    price: '3,800',
    unit: '元/月',
    priceAnnual: '38,000',
    features: [
      {
        title: '会议效率',
        items: [
          '会议纪要自动生成',
          '待办事项自动提取',
          '会议录音转文字',
          '参会人员提醒',
        ],
      },
      {
        title: '流程自动化',
        items: [
          '审批流自定义配置',
          '多级审批链支持',
          '移动端审批',
          '审批超时提醒',
        ],
      },
      {
        title: '知识管理',
        items: [
          '企业知识库构建',
          '文档智能分类',
          '语义检索',
          '权限精细控制',
        ],
      },
    ],
    useCases: [
      { title: '行政办公', description: '会议纪要、审批流程、公告发布、资产管理的全面自动化' },
      { title: '项目协作', description: '任务分配与提醒、进度追踪、风险预警、项目周报自动生成' },
      { title: '人事管理', description: '员工入职流程、考勤统计、假期管理、绩效考核辅助' },
    ],
    pricingTiers: [
      { name: '标准版', price: '3,800', description: '适合中小企业', features: ['20个账号', '会议纪要', '基础审批流', '知识库'] },
      { name: '专业版', price: '7,600', description: '适合成长型企业', features: ['100个账号', '高级审批流', 'API接口', '数据分析', '优先支持'] },
      { name: '企业版', price: '15,000', description: '适合大型组织', features: ['无限账号', '私有化部署', '深度定制', '专属顾问', '年度服务'] },
    ],
    highlight: false,
    emoji: '📋',
    color: 'green',
    colorLight: 'bg-green-50 border-green-200',
  },
  custom: {
    id: 'custom',
    name: '全定制',
    nameEn: 'Full Custom',
    tagline: 'AI Agent定制服务',
    taglineEn: 'AI Agent Custom Development',
    description: '根据企业特殊需求，深度定制AI Agent，含行业知识库搭建、私有化部署、全流程开发。专属模型微调，年度维保服务。',
    price: '30,000+',
    unit: '元起',
    priceAnnual: '30,000+',
    features: [
      {
        title: '需求分析',
        items: [
          '业务场景深度调研',
          'AI能力匹配分析',
          'ROI测算与方案制定',
          '竞品对标研究',
        ],
      },
      {
        title: '定制开发',
        items: [
          '行业知识库搭建',
          '专属模型微调',
          '私有化环境部署',
          '第三方系统集成',
        ],
      },
      {
        title: '交付与服务',
        items: [
          '完整技术文档',
          '操作培训',
          '年度维保服务',
          '7×24小时响应',
        ],
      },
    ],
    useCases: [
      { title: '垂直行业AI', description: '医疗、法律、金融、政务等高壁垒行业的专业AI Agent定制' },
      { title: '复杂流程自动化', description: '多系统集成、复杂审批链、定制化报表的端到端自动化' },
      { title: '私有化AI部署', description: '数据安全要求高、需要完全自主可控的企业私有化AI平台' },
    ],
    pricingTiers: [
      { name: '入门定制', price: '30,000', description: '适合单一场景标准化定制', features: ['单一场景Agent', '基础知识库', '公有云部署', '1年维保'] },
      { name: '深度定制', price: '80,000', description: '适合多场景复杂需求', features: ['3个场景Agent', '行业知识库', '混合部署', '1年维保+支持'] },
      { name: '战略合作', price: '面议', description: '适合长期深度合作', features: ['不限场景', '完全私有化', '专属团队', '战略咨询'] },
    ],
    highlight: false,
    emoji: '🚀',
    color: 'orange',
    colorLight: 'bg-orange-50 border-orange-200',
  },
};

export const PRODUCT_ORDER = ['zhixiaoke', 'xiaoxiaobang', 'banxiaozhu', 'custom'];

export const ALL_PRODUCTS = PRODUCT_ORDER.map((id) => PRODUCTS[id]);
